import React, { useEffect, useMemo, useState } from 'react';
import { cn, Icon } from '@lqa-frontend/ui-system';
import { AnimatePresence, motion } from 'framer-motion';
import { DropdownMenu } from '../dropdown/Dropdown';

import {
	parseYearMonth,
	padMonth,
	createDefaultYears,
} from './year-month-picker-utils';

const MONTHS = [
	'Jan',
	'Feb',
	'Mar',
	'Apr',
	'May',
	'Jun',
	'Jul',
	'Aug',
	'Sep',
	'Oct',
	'Nov',
	'Dec',
] as const;
const MONTH_ROWS = [
	[0, 1, 2, 3],
	[4, 5, 6, 7],
	[8, 9, 10, 11],
] as const;

const DOT_COLORS = {
	past: 'var(--color-green-300)',
	current: 'var(--color-brand-blue-300)',
	future: 'var(--color-text-tertiary)',
} as const;

function getDefaultDotColor(monthIndex: number, year: number): string {
	const now = new Date();
	const currentYear = now.getFullYear();
	const currentMonth = now.getMonth();

	if (year < currentYear || (year === currentYear && monthIndex < currentMonth))
		return DOT_COLORS.past;
	if (year === currentYear && monthIndex === currentMonth)
		return DOT_COLORS.current;
	return DOT_COLORS.future;
}

export type IYearMonthStatusPickerProps = {
	value: string;
	onValueChange: (value: string | null) => void;
	status: string;
	years?: number[];
	placeholder?: string;
	getDotColor?: (monthIndex: number, year: number) => string;
	/** When this returns `null`, the month cell is disabled (no roster). */
	getRosterId?: (monthIndex: number, year: number) => number | null;
	className?: string;
	triggerClassName?: string;
	statusClassName?: string;
	disabled?: boolean;
	/** Optional icon rendered before the month/year label in the trigger. */
	triggerIcon?: React.ReactNode;
	/** Replaces the default `"MMM, YYYY"` trigger label. */
	triggerLabel?: string;
};

export function YearMonthStatusPicker({
	value,
	onValueChange,
	status,
	years,
	placeholder = 'Select month',
	getDotColor = getDefaultDotColor,
	getRosterId,
	className,
	triggerClassName,
	statusClassName,
	disabled,
	triggerIcon,
	triggerLabel: triggerLabelOverride,
}: IYearMonthStatusPickerProps) {
	const parsedValue = parseYearMonth(value);
	const nowYear = new Date().getFullYear();
	const yearOptions = useMemo(
		() => (years && years.length > 0 ? years : createDefaultYears(nowYear)),
		[years, nowYear]
	);

	const [open, setOpen] = useState(false);
	const [yearAnimationDirection, setYearAnimationDirection] = useState<1 | -1>(
		1
	);
	const [selectedYear, setSelectedYear] = useState<number>(
		parsedValue?.year ?? yearOptions[0] ?? nowYear
	);

	useEffect(() => {
		if (parsedValue?.year) {
			setSelectedYear(parsedValue.year);
			return;
		}
		setSelectedYear((prev) =>
			yearOptions.includes(prev) ? prev : (yearOptions[0] ?? nowYear)
		);
	}, [nowYear, parsedValue?.year, yearOptions]);

	const triggerLabel =
		triggerLabelOverride ??
		(parsedValue
			? `${MONTHS[parsedValue.month - 1]}, ${parsedValue.year}`
			: placeholder);

	const selectedYearIndex = yearOptions.findIndex((y) => y === selectedYear);
	const canSelectPreviousYear = selectedYearIndex > 0;
	const canSelectNextYear =
		selectedYearIndex >= 0 && selectedYearIndex < yearOptions.length - 1;

	function handleSelectYear(nextYear: number) {
		setYearAnimationDirection(nextYear > selectedYear ? 1 : -1);
		setSelectedYear(nextYear);
	}

	function handleSelectMonth(monthIndex: number) {
		if (getRosterId?.(monthIndex, selectedYear) == null) return;

		const month = monthIndex + 1;
		const isAlreadySelected =
			parsedValue?.year === selectedYear && parsedValue.month === month;
		if (isAlreadySelected) {
			onValueChange(null);
		} else {
			onValueChange(`${selectedYear}-${padMonth(month)}`);
		}
		setOpen(false);
	}

	return (
		<DropdownMenu open={open} onOpenChange={setOpen}>
			<DropdownMenu.Trigger asChild>
				<button
					type="button"
					disabled={disabled}
					className={cn(
						'flex h-32 max-w-280 cursor-pointer items-center gap-8 rounded-s border bg-white px-10 py-6 shadow-[0px_1px_2px_rgba(0,0,0,0.05)] transition-colors',
						open ? 'border-interactive' : 'border-divider hover:bg-zinc-50',
						disabled && 'cursor-not-allowed opacity-50',
						triggerClassName
					)}
				>
					<div className="flex items-center gap-8">
						{triggerIcon}
						<span className="text-13 leading-lh4 text-text-primary">
							{triggerLabel}
						</span>
					</div>
					<span
						className={cn(
							'flex items-center justify-center rounded-max border px-8 py-2 text-12 font-semibold leading-lh6',
							statusClassName ||
								'border-outline-interactive bg-interactive-light text-interactive'
						)}
					>
						{status}
					</span>
				</button>
			</DropdownMenu.Trigger>

			<DropdownMenu.Content
				align="start"
				forceMount
				className={cn(
					'w-270 max-w-288 rounded-m border border-divider bg-white p-0 pb-12',
					'shadow-[0px_4px_6px_rgba(0,0,0,0.05),0px_10px_15px_rgba(0,0,0,0.1)]',
					className
				)}
			>
				<AnimatePresence>
					{open && (
						<motion.div
							key="year-month-status-picker-content"
							initial={{ opacity: 0, y: -6, scale: 0.98 }}
							animate={{ opacity: 1, y: 0, scale: 1 }}
							exit={{ opacity: 0, y: -4, scale: 0.98 }}
							transition={{ duration: 0.18, ease: 'easeOut' }}
						>
							{/* Year navigation */}
							<div className="flex h-40 items-center justify-between px-12 pb-8 pt-12">
								<button
									type="button"
									aria-label="Previous year"
									disabled={!canSelectPreviousYear}
									onClick={() =>
										canSelectPreviousYear &&
										handleSelectYear(yearOptions[selectedYearIndex - 1])
									}
									className={cn(
										'inline-flex h-28 w-28 items-center justify-center rounded-xxs border border-transparent transition-colors',
										canSelectPreviousYear
											? 'cursor-pointer hover:border-interactive hover:bg-brand-blue-50'
											: 'cursor-not-allowed opacity-40'
									)}
								>
									<Icon name="chevronLeft" />
								</button>

								<div
									className="w-72 overflow-hidden text-center"
									aria-live="polite"
								>
									<AnimatePresence custom={yearAnimationDirection} mode="wait">
										<motion.span
											key={selectedYear}
											custom={yearAnimationDirection}
											initial={{ opacity: 0, x: yearAnimationDirection * 16 }}
											animate={{ opacity: 1, x: 0 }}
											exit={{ opacity: 0, x: yearAnimationDirection * -16 }}
											transition={{ duration: 0.2, ease: 'easeOut' }}
											className="block text-14 leading-large text-text-primary"
										>
											{selectedYear}
										</motion.span>
									</AnimatePresence>
								</div>

								<button
									type="button"
									aria-label="Next year"
									disabled={!canSelectNextYear}
									onClick={() =>
										canSelectNextYear &&
										handleSelectYear(yearOptions[selectedYearIndex + 1])
									}
									className={cn(
										'inline-flex h-28 w-28 items-center justify-center rounded-xxs border border-transparent transition-colors',
										canSelectNextYear
											? 'cursor-pointer hover:border-interactive hover:bg-brand-blue-50'
											: 'cursor-not-allowed opacity-40'
									)}
								>
									<Icon name="chevronRight" />
								</button>
							</div>

							{/* Month rows — 4 per row with animated slide on year change */}
							<div className="overflow-hidden px-12">
								<AnimatePresence custom={yearAnimationDirection} mode="wait">
									<motion.div
										key={`months-${selectedYear}`}
										custom={yearAnimationDirection}
										initial={{ opacity: 0, x: yearAnimationDirection * 24 }}
										animate={{ opacity: 1, x: 0 }}
										exit={{ opacity: 0, x: yearAnimationDirection * -24 }}
										transition={{ duration: 0.2, ease: 'easeOut' }}
									>
										{MONTH_ROWS.map((row) => (
											<div key={row[0]} className="flex items-start">
												{row.map((i) => {
													const month = i + 1;
													const isSelected =
														parsedValue?.year === selectedYear &&
														parsedValue.month === month;
													const dotColor = getDotColor(i, selectedYear);
													const isMonthDisabled =
														getRosterId != null &&
														getRosterId(i, selectedYear) == null;
													return (
														<button
															key={MONTHS[i]}
															type="button"
															disabled={isMonthDisabled}
															aria-label={`${MONTHS[i]} ${selectedYear}`}
															aria-pressed={isSelected}
															aria-disabled={isMonthDisabled}
															onClick={() => handleSelectMonth(i)}
															className={cn(
																'flex h-36 flex-1 items-center justify-center gap-4 rounded-s py-8 transition-colors',
																isMonthDisabled
																	? 'cursor-not-allowed opacity-30'
																	: 'cursor-pointer',
																!isMonthDisabled &&
																	isSelected &&
																	'bg-interactive-light',
																!isMonthDisabled &&
																	!isSelected &&
																	'hover:bg-zinc-50'
															)}
														>
															<span className="text-14 leading-large text-text-primary">
																{MONTHS[i]}
															</span>
															<span
																className="h-8 w-8 shrink-0 rounded-max"
																style={{ background: dotColor }}
															/>
														</button>
													);
												})}
											</div>
										))}
									</motion.div>
								</AnimatePresence>
							</div>
						</motion.div>
					)}
				</AnimatePresence>
			</DropdownMenu.Content>
		</DropdownMenu>
	);
}
